import React, {
  DependencyList,
  EffectCallback,
  useCallback,
  useEffect,
  useRef,
  useState,
  WheelEventHandler,
} from "react";
import Slider from "react-slick";
import { Box, Grid, Stack, Typography } from "@mui/material";
import _ from "lodash";
import { SLIDES } from "./constants";
import SlideCard from "../slide-card";
import styles from "./styles";

export function useLazyEffect(
  effect: EffectCallback,
  deps: DependencyList = [],
  wait = 1600
) {
  const cleanUp = useRef<void | (() => void)>();
  const effectRef = useRef<EffectCallback>();
  const updatedEffect = useCallback(effect, deps);
  effectRef.current = updatedEffect;
  const lazyEffect = useCallback(
    _.debounce(() => {
      cleanUp.current = effectRef.current?.();
    }, wait),
    []
  );
  useEffect(lazyEffect, deps);
  useEffect(() => {
    return () => {
      cleanUp && cleanUp?.current && cleanUp?.current();
    };
  }, []);
}

const Carousel = () => {
  const [preventScroll, setPreventScroll] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const carouselRef = useRef<Slider>(null);

  const settings = {
    infinite: false,
    slidesToScroll: 1,
    variableWidth: true,
    initialSlide: 0,
    centerMode: true,
    arrows: false,
    speed: 500,
    easing: "easeOutQuint",
    useTransform: true,
    afterChange(currentSlide: number) {
      setCurrentSlide(currentSlide);
    },
  };

  useEffect(() => {
    const preventDefault = (e: WheelEvent) => {
      const carouselNode = document.getElementById("beneficios-slider");
      const carouselRect = carouselNode?.getBoundingClientRect();
      const viewportMiddle = window.innerHeight / 2;
      console.log(e.deltaY);
      const allowScrollDown =
        e.deltaY > 0 && currentSlide === SLIDES.length - 1;
      const allowScrollUp = e.deltaY < 0 && currentSlide === 0;
      // Check if the carousel is in the middle of the viewport
      if (
        carouselRect &&
        carouselRect.top < viewportMiddle &&
        carouselRect.bottom > viewportMiddle
      ) {
        // Prevent default scrolling behavior
        if (!allowScrollDown && !allowScrollUp) {
          e.preventDefault();
          setPreventScroll(true);
        }
      } else {
        setPreventScroll(false);
      }
    };
    window.addEventListener("wheel", preventDefault, { passive: false });
    return () => {
      window.removeEventListener("wheel", preventDefault);
    };
  }, [currentSlide, setPreventScroll]);

  const handleScroll: WheelEventHandler = useCallback(
    (event) => {
      if (preventScroll) {
        if (event.deltaY > 0) {
          carouselRef.current?.slickNext();
        } else if (event.deltaY < 0) {
          carouselRef.current?.slickPrev();
        }
      }
    },
    [preventScroll]
  );

  return (
    <Box
      sx={{
        ...styles.sectionContainer,
        backgroundImage: `url(${SLIDES[currentSlide].image})`,
      }}
      id={"beneficios-slider"}
      onWheel={handleScroll}
    >
      <Stack sx={styles.content}>
        <Grid container sx={{ justifyContent: { xs: "center", md: "" } }}>
          <Typography sx={styles.sectionTitle}>Beneficios </Typography>
          <Typography sx={styles.sectionTitleAccent}> Exclusivos</Typography>
        </Grid>

        <Slider {...settings} ref={carouselRef}>
          {SLIDES.map((slide, index) => (
            <SlideCard
              id={slide.id}
              cardTextTitle={slide.title}
              cardImg={slide.image}
              selected={false}
              slideNumber={index < 10 ? `0${index + 1}` : index}
              cardText={slide.description}
              current={slide.id === currentSlide}
            />
          ))}
        </Slider>
      </Stack>
    </Box>
  );
};

export default Carousel;
